import { getApiEndpoint } from '@/config/api';

export type CheckoutPlan = 'monthly' | 'annual';

export interface CreateCheckoutResponse {
  url: string;
}

const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * Cria sessão de checkout no gateway. O pagamento é feito na Stripe (redirecionamento).
 * O backend garante que o usuário tenha um customer no gateway antes de criar a sessão.
 */
export const createCheckoutSession = async (plan: CheckoutPlan): Promise<{ url: string }> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Usuário não autenticado');
  }
  const response = await fetch(getApiEndpoint('api/subscription/create-checkout'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ plan }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Erro ao criar sessão de checkout');
  }
  const data = await response.json();
  if (!data.url && data.url_fallback) {
    return { url: data.url_fallback };
  }
  if (!data.url) {
    throw new Error('URL de checkout não retornada');
  }
  return { url: data.url };
};

/**
 * Confirma a assinatura vinculando o preapproval_id ao usuário logado.
 * Chamado quando o MP redireciona de volta com preapproval_id na URL.
 */
export const confirmSubscription = async (preapprovalId: string): Promise<void> => {
  const token = getAuthToken();
  if (!token || !preapprovalId) return;
  try {
    await fetch(getApiEndpoint('api/subscription/confirm'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ preapprovalId }),
    });
  } catch {
    // Silencioso — o webhook ainda pode resolver a correlação
  }
};

/**
 * Obtém a URL do portal de assinatura (Stripe Customer Portal) para o usuário
 * gerenciar conta, forma de pagamento e assinatura. Redireciona para o Stripe.
 */
export const getPortalUrl = async (): Promise<{ url: string }> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Usuário não autenticado');
  }
  const response = await fetch(getApiEndpoint('api/subscription/portal'), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Erro ao abrir portal de assinatura');
  }
  const data = await response.json();
  if (!data.url) {
    throw new Error('URL do portal não retornada');
  }
  return { url: data.url };
};
